import React from 'react';
import QuickReplyButtons from './QuickReplyButtons';
import './MessageBubble.css';

const PHONE_NUMBER = '+919595562223';
const WHATSAPP_NUMBER = '919595562223';
const PHONE_REGEX = /(\+?91[-.\s]?)?[6-9]\d{9}|\+919595562223|9595562223/g;

function parseContentWithLinks(text) {
  const matches = [...text.matchAll(new RegExp(PHONE_REGEX.source, 'g'))];

  if (matches.length === 0) return text;

  const result = [];
  let lastIndex = 0;

  matches.forEach((match, i) => {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    result.push(
      <span key={`phone-${i}`} className="contact-actions">
        <a href={`tel:${PHONE_NUMBER}`} className="contact-link call-link" title="Call now">
          📞 {match[0]}
        </a>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener noreferrer"
          className="contact-link whatsapp-link"
          title="WhatsApp"
        >
          WhatsApp
        </a>
      </span>
    );
    lastIndex = match.index + match[0].length;
  });

  if (lastIndex < text.length) result.push(text.slice(lastIndex));
  return result;
}

const MessageBubble = ({ message, showTimestamp = true, showAvatar = true, onSuggestionClick }) => {
  const { content, sender, timestamp, type, metadata, suggestions } = message;

  const formatTimestamp = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffInMinutes = Math.floor((now - messageDate) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return messageDate.toLocaleDateString();
    }
  };

  const renderMessageContent = () => {
    switch (type) {
      case 'text':
        return (
          <div className="message-text-container">
            <div className="message-text">
              {sender === 'bot' ? parseContentWithLinks(content) : content}
            </div>
            {/* Show personalization indicator for bot messages */}
            {sender === 'bot' && metadata?.isPersonalized && (
              <div className="personalization-indicator">
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm3.5 6L7 10.5 4.5 8l1-1L7 8.5 10.5 5l1 1z" fill="currentColor"/>
                </svg>
                <span>Personalized for you</span>
              </div>
            )}
            {/* Show suggestions for bot messages */}
            {sender === 'bot' && suggestions && suggestions.length > 0 && (
              <QuickReplyButtons
                suggestions={suggestions}
                onSuggestionClick={onSuggestionClick}
                disabled={false}
              />
            )}
          </div>
        );
      
      case 'quick-reply':
        return (
          <div className="message-quick-reply">
            <div className="message-text">{content}</div>
            {/* Remove duplicate suggestion handling - use the QuickReplyButtons component instead */}
          </div>
        );
      
      case 'card':
        return (
          <div className="message-card">
            {metadata?.title && (
              <div className="card-title">{metadata.title}</div>
            )}
            <div className="message-text">{content}</div>
            {metadata?.actions && (
              <div className="card-actions">
                {metadata.actions.map((action, index) => (
                  <button
                    key={index}
                    className="action-button"
                    onClick={() => action.onClick?.()}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      
      default:
        return <div className="message-text">{content}</div>;
    }
  };

  return (
    <div className={`message-bubble-container ${sender}`}>
      {showAvatar && sender === 'bot' && (
        <div className="message-avatar">
          <div className="avatar-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 3C11 5.5 11 9 12 11C13 9 13 5.5 12 3Z" fill="currentColor"/>
              <path d="M8.5 5.5C7 8 8 11.5 10.5 13C10.5 10.5 9.5 7.5 8.5 5.5Z" fill="currentColor" opacity="0.85"/>
              <path d="M15.5 5.5C17 8 16 11.5 13.5 13C13.5 10.5 14.5 7.5 15.5 5.5Z" fill="currentColor" opacity="0.85"/>
              <path d="M5.5 8.5C4.5 11 6 14 9 14.5C8.5 12 7 9.5 5.5 8.5Z" fill="currentColor" opacity="0.7"/>
              <path d="M18.5 8.5C19.5 11 18 14 15 14.5C15.5 12 17 9.5 18.5 8.5Z" fill="currentColor" opacity="0.7"/>
              <path d="M7.5 15.5C9 14 10.5 13.5 12 13.5C13.5 13.5 15 14 16.5 15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="12" y1="15.5" x2="12" y2="20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M12 19C10.5 18 9 18 8 19C9 20 10.5 20 12 19Z" fill="currentColor" opacity="0.7"/>
              <path d="M12 19C13.5 18 15 18 16 19C15 20 13.5 20 12 19Z" fill="currentColor" opacity="0.7"/>
            </svg>
          </div>
        </div>
      )}
      
      <div className="message-content-wrapper">
        <div className={`message-bubble ${sender} ${type}`}>
          {renderMessageContent()}
        </div>
        
        {showTimestamp && (
          <div className="message-timestamp">
            {formatTimestamp(timestamp)}
            {sender === 'user' && metadata?.status && (
              <span className={`message-status ${metadata.status}`}>
                {metadata.status === 'sent' && '✓'}
                {metadata.status === 'delivered' && '✓✓'}
                {metadata.status === 'read' && '✓✓'}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;